/**
 * Single Recommendation Card
 *
 * Displays one recommendation with a type-specific icon, description,
 * and a RatingWidget. The content is extracted from the polymorphic
 * JSONB payload based on the recommendation type.
 *
 * Type → Icon mapping:
 *   RELATED_NOTE    → Link2 (link to existing note)
 *   RESEARCH        → BookOpen (research suggestion)
 *   ACTION_ITEM     → CheckSquare (actionable step)
 *   TOPIC_EXPANSION → Expand (deeper exploration)
 */

import {
  Link2,
  BookOpen,
  CheckSquare,
  Expand,
  type LucideIcon,
} from "lucide-react";
import type {
  Recommendation,
  RecommendationRating,
  RecommendationType,
} from "../../types/recommendation";
import { REC_TYPE_LABELS } from "../../types/recommendation";
import { RatingWidget } from "./RatingWidget";

interface RecommendationCardProps {
  recommendation: Recommendation;
  onRate: (recId: string, rating: RecommendationRating) => void;
  disabled?: boolean;
}

const TYPE_ICONS: Record<RecommendationType, LucideIcon> = {
  RELATED_NOTE: Link2,
  RESEARCH: BookOpen,
  ACTION_ITEM: CheckSquare,
  TOPIC_EXPANSION: Expand,
};

const TYPE_COLORS: Record<RecommendationType, string> = {
  RELATED_NOTE: "text-accent-blue",
  RESEARCH: "text-accent-purple",
  ACTION_ITEM: "text-accent-orange",
  TOPIC_EXPANSION: "text-accent-cyan",
};

/**
 * Extract a display title and description from the polymorphic content.
 * Each rec_type has a different JSONB schema, so we pull the most relevant
 * fields for display.
 */
function extractDisplay(rec: Recommendation): {
  title: string;
  description: string;
} {
  const c = rec.content;
  switch (rec.type) {
    case "RELATED_NOTE":
      return {
        title: "Related Note",
        description: (c.explanation as string) || "Similar content detected",
      };
    case "RESEARCH":
      return {
        title: (c.suggestion as string) || "Research suggestion",
        description: (c.reasoning as string) || "",
      };
    case "ACTION_ITEM":
      return {
        title: (c.action as string) || "Action item",
        description: (c.context as string) || "",
      };
    case "TOPIC_EXPANSION":
      return {
        title: (c.topic as string) || "Topic to explore",
        description: (c.reasoning as string) || "",
      };
    default:
      return { title: "Recommendation", description: "" };
  }
}

export function RecommendationCard({
  recommendation,
  onRate,
  disabled,
}: RecommendationCardProps) {
  const Icon = TYPE_ICONS[recommendation.type];
  const iconColor = TYPE_COLORS[recommendation.type];
  const typeLabel = REC_TYPE_LABELS[recommendation.type];
  const { title, description } = extractDisplay(recommendation);

  // Extract keywords for tag pills (research type has keywords array)
  const keywords =
    recommendation.type === "RESEARCH"
      ? (recommendation.content.keywords as string[]) || []
      : [];

  // Extract priority badge (action_item type has priority field)
  const priority =
    recommendation.type === "ACTION_ITEM"
      ? (recommendation.content.priority as string) || null
      : null;

  return (
    <div className="flex items-start gap-3 py-3 px-3 rounded-md bg-bg-primary/50 border border-border/50 hover:border-border transition-colors">
      {/* Type icon */}
      <div className="shrink-0 p-1.5 rounded-md bg-bg-secondary">
        <Icon className={`w-4 h-4 ${iconColor}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-mono text-text-muted uppercase">
            {typeLabel}
          </span>
          {priority && (
            <span
              className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                priority === "high"
                  ? "bg-accent-red/15 text-accent-red"
                  : priority === "medium"
                    ? "bg-accent-yellow/15 text-accent-yellow"
                    : "bg-text-muted/15 text-text-muted"
              }`}
            >
              {priority}
            </span>
          )}
        </div>
        <p className="text-xs font-semibold text-text-primary leading-snug truncate">
          {title}
        </p>
        {description && (
          <p className="text-[11px] text-text-muted leading-snug mt-0.5 line-clamp-2">
            {description}
          </p>
        )}
        {/* Keyword tags (for research type) */}
        {keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {keywords.slice(0, 4).map((kw) => (
              <span
                key={kw}
                className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-accent-purple/10 text-accent-purple"
              >
                {kw}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Rating widget */}
      <div className="shrink-0">
        <RatingWidget
          value={recommendation.rating}
          onChange={(rating) => onRate(recommendation.id, rating)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
